// 3D Game Board constants

export const TILE_SIZE = 1;
export const WALL_HEIGHT = 0.65;
export const FLOOR_HEIGHT = 0.1;
export const OBSTACLE_HEIGHT = 0.15;
export const PORTAL_HEIGHT = 0.12;
export const GAP = 0.02;

// Colors matching GameBoard Tile3D.getColors()
export const TILE_COLORS = {
  floor: {
    light: '#F5F5F5',
    dark: '#E8E8E8',
    side: '#BDBDBD',
  },
  floorPath: {
    light: '#FFF8DC',
    dark: '#FAEBD7',
    side: '#DEB887',
  },
  wall: {
    top: '#9E9E9E',
    front: '#757575',
    side: '#616161',
  },
  lava: {
    top: '#FF7043',
    front: '#FF5722',
    side: '#E64A19',
    glow: '#FF5722',
  },
  stop: {
    top: '#4DD0E1',
    front: '#00BCD4',
    side: '#0097A7',
    glow: '#00BCD4',
  },
  teleporter: {
    top: '#CE93D8',
    front: '#AB47BC',
    side: '#8E24AA',
    glow: '#AB47BC',
  },
  start: {
    top: '#81C784',
    front: '#66BB6A',
    side: '#4CAF50',
    glow: '#4CAF50',
    dark: '#388E3C',
  },
  end: {
    top: '#64B5F6',
    front: '#42A5F5',
    side: '#2196F3',
    glow: '#2196F3',
    dark: '#1565C0',
  },
} as const;

// Character color configs (matching GameBoard CHARACTER_CONFIGS)
export const CHARACTER_3D_COLORS: Record<string, string> = {
  default_hero: '#FF6B6B',
  tiny_blue: '#4a90e2',
  chonky_green: '#4caf50',
  sleek_purple: '#9c27b0',
  golden_glow: '#ffc107',
};

// Full 6-face color configs matching the 2D CHARACTER_CONFIGS gradients
export interface CharacterFaceConfig {
  front: string; back: string; top: string; bottom: string; left: string; right: string;
  eyeSize: number; eyeGap: number; mouthWidth: number; scale: number; backSymbol: string;
}

export const CHARACTER_FACE_COLORS: Record<string, CharacterFaceConfig> = {
  default_hero: {
    front: '#ff6b7a', back: '#cc4455', top: '#ff8a95', bottom: '#aa3344',
    left: '#ee5a6f', right: '#ee5a6f',
    eyeSize: 1, eyeGap: 1, mouthWidth: 1, scale: 1, backSymbol: '+',
  },
  tiny_blue: {
    front: '#4a90e2', back: '#2968a3', top: '#5ba0f2', bottom: '#1e5189',
    left: '#3570c0', right: '#3a75c5',
    eyeSize: 1.4, eyeGap: 1, mouthWidth: 0.9, scale: 0.62, backSymbol: '+',
  },
  chonky_green: {
    front: '#4caf50', back: '#2e7d32', top: '#66bb6a', bottom: '#1b5e20',
    left: '#3b9040', right: '#409843',
    eyeSize: 0.6, eyeGap: 1.4, mouthWidth: 1.2, scale: 1.06, backSymbol: '+',
  },
  sleek_purple: {
    front: '#9c27b0', back: '#6a1b9a', top: '#ab47bc', bottom: '#4a148c',
    left: '#7e22a0', right: '#8123a2',
    eyeSize: 0.85, eyeGap: 0.9, mouthWidth: 0.85, scale: 0.96, backSymbol: '+',
  },
  golden_glow: {
    front: '#ffc107', back: '#ff8f00', top: '#ffca28', bottom: '#e65100',
    left: '#ffa300', right: '#ffa800',
    eyeSize: 1, eyeGap: 1, mouthWidth: 1, scale: 0.94, backSymbol: '*',
  },
};

// Collectible colors
export const COLLECTIBLE_COLORS = {
  coin: '#FFD700',
  gem: '#AB47BC',
} as const;

// Enemy type shapes and colors (matching 2D ENEMY_SHAPE_CONFIG)
export const ENEMY_COLORS = {
  beetle: { body: '#7cd97c', accent: '#2b722b', eye: '#1a1a2e' },
  drone: { body: '#b8a88e', accent: '#40382c', eye: '#1a1a2e' },
  mite: { body: '#96d8f0', accent: '#187396', eye: '#1a1a2e' },
  hound: { body: '#f0724a', accent: '#681808', eye: '#FFFF00' },
  mirror: { body: '#d4a0ff', accent: '#601ca0', eye: '#1a1a2e' },
} as const;

// Enemy shape ratios (matching 2D widthRatio/heightRatio/depthRatio)
export const ENEMY_SHAPE_RATIOS = {
  beetle: { w: 1.15, h: 0.85, d: 0.9, radius: 0.05 },
  drone: { w: 0.75, h: 1.25, d: 0.7, radius: 0.05 },
  mite: { w: 0.8, h: 0.8, d: 0.8, radius: 0.35 },
  hound: { w: 1.1, h: 0.9, d: 1.05, radius: 0.05 },
  mirror: { w: 1.0, h: 1.0, d: 0.5, radius: 0.05 },
} as const;

// World theme colors for backgrounds (sky + fog + ground)
export const WORLD_THEME_COLORS: Record<string, { sky: string; fog: string; ground: string; ambient: string }> = {
  greenwood: { sky: '#1a3a2a', fog: '#2a5a3a', ground: '#2d4a2d', ambient: '#84fab0' },
  stonehollow: { sky: '#1a1e24', fog: '#2a3040', ground: '#3a3a3a', ambient: '#b0becf' },
  frostline: { sky: '#0e2a3a', fog: '#1a4a5a', ground: '#2a4a5a', ambient: '#cbefff' },
  emberdeep: { sky: '#2a1008', fog: '#4a2010', ground: '#3a1a0a', ambient: '#ffba74' },
  arcana: { sky: '#0e0a2a', fog: '#1a1050', ground: '#1a1040', ambient: '#a55eea' },
};
